/**
 * "Sign in with Google" / "Sign in with Microsoft" buttons for the
 * Triveny.ai login page.
 *
 * Queries /api/platform/auth/oauth/providers on mount to know which
 * providers are configured on this deployment. Buttons for unconfigured
 * providers are hidden so the UI never shows a CTA that returns 503.
 *
 * Clicking a button navigates the browser to the /start endpoint, which
 * returns a 302 to the IdP authorize URL. The user completes auth at
 * the IdP, the IdP redirects back to /callback, and the callback
 * eventually lands on /auth/oauth-success with the JWT in the query.
 *
 * Renders nothing for WeaveIn (config.isTriveny gate handled by caller).
 */
import { useEffect, useState } from 'react';
import { config } from '@/src/config';

interface ProvidersResponse {
  google: boolean;
  microsoft: boolean;
}

interface OAuthProviderButtonsProps {
  /** Where to send the user after a successful sign-in. Defaults to "/". */
  returnTo?: string;
}

function start(provider: 'google' | 'microsoft', returnTo: string) {
  const params = new URLSearchParams({ return_to: returnTo });
  // We send the browser straight to the backend /start endpoint so the
  // backend can issue a 302 to the IdP. Using window.location avoids the
  // cross-origin cookie issues a fetch() would hit.
  window.location.href = `${config.weaveinApi.baseUrl}/api/platform/auth/oauth/${provider}/start?${params}`;
}

export function OAuthProviderButtons({ returnTo = '/' }: OAuthProviderButtonsProps) {
  const [providers, setProviders] = useState<ProvidersResponse | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${config.weaveinApi.baseUrl}/api/platform/auth/oauth/providers`);
        if (!res.ok) return;
        const data = (await res.json()) as ProvidersResponse;
        if (!cancelled) setProviders(data);
      } catch {
        /* providers list optional — silent fail just hides the buttons */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!providers || (!providers.google && !providers.microsoft)) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 4 }}>
      {/* Divider with "or" */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '8px 0 2px' }}>
        <span style={{ flex: 1, height: 1, background: 'rgba(0,0,0,0.08)' }} />
        <span
          style={{
            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            fontSize: 10,
            color: '#6b6b6b',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
          }}
        >
          or sign in with
        </span>
        <span style={{ flex: 1, height: 1, background: 'rgba(0,0,0,0.08)' }} />
      </div>

      {providers.google && (
        <button
          type="button"
          onClick={() => start('google', returnTo)}
          style={btnStyle}
          aria-label="Sign in with Google"
        >
          <GoogleMark />
          <span style={btnLabelStyle}>Continue with Google</span>
        </button>
      )}

      {providers.microsoft && (
        <button
          type="button"
          onClick={() => start('microsoft', returnTo)}
          style={btnStyle}
          aria-label="Sign in with Microsoft"
        >
          <MicrosoftMark />
          <span style={btnLabelStyle}>Continue with Microsoft</span>
        </button>
      )}
    </div>
  );
}

const btnStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  width: '100%',
  height: 40,
  padding: '0 14px',
  background: '#ffffff',
  border: '1px solid rgba(0,0,0,0.12)',
  borderRadius: 8,
  cursor: 'pointer',
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, system-ui, sans-serif",
  fontSize: 13.5,
  fontWeight: 500,
  color: '#1a1a1a',
  transition: 'background-color 120ms ease, border-color 120ms ease',
};

const btnLabelStyle: React.CSSProperties = {
  flex: 1,
  textAlign: 'center',
};

// Official Google "G" mark — simplified inline SVG, brand-compliant colors.
function GoogleMark() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden focusable="false">
      <path
        fill="#FFC107"
        d="M43.6 20.5H42V20H24v8h11.3c-1.7 4.8-6.3 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.4-.4-3.5z"
      />
      <path
        fill="#FF3D00"
        d="M6.3 14.7l6.6 4.8C14.6 16.1 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.2 0 9.9-2 13.5-5.2l-6.2-5.2c-2 1.5-4.6 2.4-7.3 2.4-5 0-9.4-3.2-11.1-7.7l-6.5 5C9.6 39.5 16.2 44 24 44z"
      />
      <path
        fill="#1976D2"
        d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.3 5.7l6.2 5.2C40.8 36.2 44 30.6 44 24c0-1.2-.1-2.4-.4-3.5z"
      />
    </svg>
  );
}

// Microsoft 4-square logo mark.
function MicrosoftMark() {
  return (
    <svg width="16" height="16" viewBox="0 0 21 21" aria-hidden focusable="false">
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}
